import React, { useState } from 'react';
import { useSetAtom } from 'jotai';
import { message } from 'antd';
import { ActionBubbleBarWrapper, ActionBubbleLabel } from './styled';
import ActionBubble from './ActionBubble';
import ActionInputDialog from './ActionInputDialog';
import { SuggestedAction } from './types';
import { suggestedActionsAtom } from '../NexaStore';
import { NEXA_ACTION_BUBBLE_EXECUTE, ActionBubbleEventDetail } from '../utils';
import { logger } from '@src/utils/misc/logger';
import apptracker from '@src/utils/sensor/apptracker';
import { NEXA_ACTION_SHOWN, NEXA_ACTION_SELECT } from '@src/utils/sensor/track';

interface ActionBubbleBarProps {
  actions: SuggestedAction[];
}

/**
 * Build a prompt string from a template and parameters.
 * Template uses {{param}} syntax for interpolation.
 * Example: "Break down by {{dimension}}" + {dimension: "Device"} => "Break down by Device"
 */
function buildPromptFromTemplate(
  template: string | undefined,
  params: Record<string, any>,
): string | null {
  if (!template) {
    return null;
  }

  let result = template;
  for (const [key, value] of Object.entries(params)) {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder.replace(/[{}]/g, '\\$&'), 'g'), String(value));
  }

  // Check if any placeholders remain unresolved
  if (/\{\{\w+\}\}/.test(result)) {
    logger.warn('[ActionBubbles] Prompt template has unresolved placeholders:', result);
    return null;
  }

  return result;
}

/**
 * Dispatch a custom event to execute an action query.
 * This allows ChatBox to handle the fetchStream call, avoiding
 * the race condition where ActionBubbleBar unmounts before the request completes.
 */
function dispatchActionBubbleExecute(query: string) {
  const event = new CustomEvent<ActionBubbleEventDetail>(NEXA_ACTION_BUBBLE_EXECUTE, {
    detail: { query },
  });
  window.dispatchEvent(event);
}

const ActionBubbleBar: React.FC<ActionBubbleBarProps> = ({ actions }) => {
  const [inputDialogVisible, setInputDialogVisible] = useState(false);
  const [selectedAction, setSelectedAction] = useState<SuggestedAction | null>(null);
  const setSuggestedActions = useSetAtom(suggestedActionsAtom);

  React.useEffect(() => {
    logger.log('[ActionBubbles] ActionBubbleBar received actions:', actions);
    if (actions.length > 0) {
      apptracker(NEXA_ACTION_SHOWN, {
        actions: actions.map((a) => ({
          action_id: a.action_id,
          type: a.type,
          needs_input: a.needs_input,
        })),
        count: actions.length,
      });
    }
  }, [actions]);

  const handleOpenInputDialog = (action: SuggestedAction) => {
    setSelectedAction(action);
    setInputDialogVisible(true);
  };

  const handleExecuteAgent = async (action: SuggestedAction, userInput?: Record<string, any>) => {
    try {
      logger.log('[ActionBubbles] Executing agent action:', action.action_id);

      // Merge user input with existing params
      const finalParams = userInput ? { ...action.params, ...userInput } : action.params;
      logger.log('[ActionBubbles] Final params:', finalParams);

      // Build prompt from template
      const prompt = buildPromptFromTemplate(action.prompt_template, finalParams);

      if (prompt) {
        logger.log('[ActionBubbles] Sending prompt to agent:', prompt);
        // Dispatch event to ChatBox which handles fetchStream
        dispatchActionBubbleExecute(prompt);
      } else {
        // Fallback: use the action label as the prompt
        logger.log('[ActionBubbles] No template, using label as prompt:', action.label);
        dispatchActionBubbleExecute(action.label);
      }

      setSuggestedActions([]);
    } catch (error) {
      logger.error('[ActionBubbles] Failed to execute action:', error);
      message.error('Failed to execute action');
    }
  };

  const handleInputDialogSubmit = (params: Record<string, any>) => {
    if (selectedAction) {
      apptracker(NEXA_ACTION_SELECT, {
        action_id: selectedAction.action_id,
        selected_values: params,
      });
      handleExecuteAgent(selectedAction, params);
    }
    setInputDialogVisible(false);
    setSelectedAction(null);
  };

  const handleInputDialogCancel = () => {
    setInputDialogVisible(false);
    setSelectedAction(null);
  };

  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      <ActionBubbleBarWrapper>
        <ActionBubbleLabel>Try suggested actions below or continue in chat</ActionBubbleLabel>
        {actions.map((action) => (
          <ActionBubble
            key={action.action_id}
            action={action}
            onOpenInputDialog={handleOpenInputDialog}
            onExecuteAgent={handleExecuteAgent}
          />
        ))}
      </ActionBubbleBarWrapper>
      <ActionInputDialog
        visible={inputDialogVisible}
        action={selectedAction}
        onSubmit={handleInputDialogSubmit}
        onCancel={handleInputDialogCancel}
      />
    </>
  );
};

export default ActionBubbleBar;
